import { useEffect, useMemo, useState } from "react";
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import VersionBox from '../../components/common/VersionBox';
import EditInput from '../../components/forms/EditInput';
import GeneralSection from '../../components/common/GeneralSection';
import HorizontalFormContainer from '../../components/forms/HorizontalFormContainer';
import UserSelectedIconSelect from '../../components/pages/UserSelectedIconSelect';
import PreviewServicePlanning from '../../components/preview/PreviewServicePlanning';

import reduxActions from '../../actions'
import i18n from "../../i18n";
import ManagedInput from "../../components/forms/ManagedInput";

const ServicePlanningModule = (parentProps) => {
    const [state, setState] = useState({ menuItem: {}, module: {}, showPreview: true, ready: false });
    const { projectId } = useParams();

    const reduxState = useSelector(state => {
        return {
            modules: state.modules,
            versions: state.versions,
            menuItems: state.menuItems,
            flags: state.flags,
            alerts: state.alerts,
        }
    });
    const dispatch = useDispatch();

    const actions = useMemo(() => bindActionCreators({
        getProjectVersion: reduxActions.getProjectVersion,
        getProjectVersionModules: reduxActions.getProjectVersionModules,
        getProjectVersionMenuItems: reduxActions.getProjectVersionMenuItems,
        getModuleByType: reduxActions.getModuleByType,
        clearAlert: reduxActions.clearAlert,
        addAlert: reduxActions.addAlert,
        updateMenuItemParameter: reduxActions.updateMenuItemParameter,
        updateModule: reduxActions.updateModule,
        updateModuleParameter: reduxActions.updateModuleParameter
    },
        dispatch),
        []
    );

    const props = { ...reduxState, ...parentProps };
    const modulesInProgress = props.modules.inProgress;
    const menuItemsInProgress = props.menuItems.inProgress;
    const selectModule = { isMe: props.modules?.select?.moduleType === 13, enabled: props.modules?.select?.enabled };
    const isFeatureEnabled = props.flags.results?.servicePlanning;

    // do on mount
    useEffect(() => {
        actions.getProjectVersion(projectId);
        actions.getProjectVersionModules(projectId);
        actions.getProjectVersionMenuItems(projectId);
        actions.getModuleByType(projectId, 13);
        actions.clearAlert('module-service-planning-disabled');
    }, []);

    // update module state for module selection
    useEffect(() => {
        if (!modulesInProgress && isFeatureEnabled) {
            setState((prevState) => {
                return {
                    ...prevState,
                    module: props.modules.select
                };
            });
        }

        if (!menuItemsInProgress && !modulesInProgress && isFeatureEnabled) {
            const menuItems = props.menuItems.data;
            const moduleType = props.modules.select.moduleType;
            const menuItem = menuItems.find(item => item.type == 1 && item.subType == moduleType);
            if (menuItem !== undefined) {
                setState((prevState) => {
                    return {
                        ...prevState,
                        menuItem,
                        ready: true,
                    };
                });
            }
            else {
                setState((prevState) => {
                    return {
                        ...prevState,
                        ready: true,
                    };
                });
            }
        }

        if (!menuItemsInProgress && !modulesInProgress) {
            setState((prevState) => {
                return {
                    ...prevState,
                    ready: true,
                };
            });
        }
    }, [modulesInProgress, menuItemsInProgress, props.modules.select, props.menuItems.data, props.modules.select?.moduleType, isFeatureEnabled]);

    // manage alerts
    useEffect(() => {
        if (state.ready && selectModule.isMe) {
            if (!selectModule.enabled) {
                actions.addAlert({
                    message: i18n.t('modules.servicePlanning.error1'),
                    linkText: i18n.t('enableHere'),
                    link: `/projects/${projectId}/modules/enable-modules`,
                    view: '/modules/service-planning',
                    type: 'warning',
                    id: 'module-service-planning-disabled',
                });
            }
            else {
                actions.clearAlert('module-service-planning-disabled');
            }
        }
    }, [state.ready, props.modules?.select]);

    // event handlers
    const updateMenuItemFromEvent = (evt) => {
        const fieldName = evt.target.name;
        const changedValue = evt.target.value.trim().length > 0 ? evt.target.value.trim() : i18n.t('modules.servicePlanning.servicePlanning');
        if (changedValue !== '') {
            setState((prevState) => {
                return {
                    ...prevState,
                    menuItem: {
                        ...prevState.menuItem,
                        title: changedValue,
                    },
                };
            });

            actions.updateMenuItemParameter(
                projectId,
                state.module.moduleType,
                1,
                fieldName,
                changedValue,
            );
        }
    }

    const updateModuleParameterFromEvent = (evt) => {
        const fieldName = evt.target.name;
        const changedValue = evt.target.value.trim();
        setState((prevState) => {
            const newState = {
                ...prevState,
                module: {
                    ...prevState.module,
                    [fieldName]: changedValue,
                },
            };
            return newState;
        });

        actions.updateModuleParameter(projectId, state.module.moduleType, fieldName, changedValue);
    };

    //Preview ServicePlanning
    const activeCheckin = () => {
        let checkInModule = props.modules?.data?.filter((module) => { return module.moduleType === constants.ModuleType.find((m) => { return m.Name === 'Check-In'; }).Id; });
        return checkInModule.filter((module) => { return module.enabled === true; });
    }
    const { select } = props.versions;
    const previewServicePlanning = state.module?.enabled && (
        <PreviewServicePlanning
            module={state.module}
            menuItem={state.menuItem}
            baseColorTheme={select.baseColorTheme}
            actionColor={select?.actionColor?.substring(1)}
            accentColor={select?.accentColor?.substring(1)}
            secondaryText={select?.secondaryText?.substring(1)}
            primaryText={select?.primaryText?.substring(1)}
            menuStyle={select.menuStyle}
            menuItems={props.menuItems.data}
            checkin={
                props.flags.results?.chMSIntegration
                && props.flags.results?.chMSCheckIn
                && activeCheckin.length > 0
            }
        />
    );
    // build form
    const bodyForm = state.module?.enabled && (

        <div className="form-wrapper">
            <h1
                data-qa="service-planning-page-header"
                id="service-planning-page-header"
            >
                {i18n.t('modules.servicePlanning.title')}
            </h1>
            {props.flags.results?.scPeople
                && (
                    <p
                        data-qa="service-planning-page-subhead"
                        id="service-planning-page-subhead"
                        className="lead"
                    >
                        {i18n.t('modules.servicePlanning.subtitle')}
                    </p>
                )}
            <GeneralSection
                name={i18n.t('modules.servicePlanning.section1')}
                sectionClass="edit-module-name module-display-settings-list"
            >
                <div
                    id="module-display-setting"
                    className="module-display-setting"
                >
                    <EditInput
                        containerId="edit-module-name"
                        containerClass="edit-module-name-wrap"
                        name="title"
                        labelText={i18n.t('name')}
                        value={state.menuItem.title ? state.menuItem.title : ''}
                        charLimit={16}
                        onBlur={updateMenuItemFromEvent}
                        onReturnDown={updateMenuItemFromEvent}
                        actionClass="edit-module-name-action"
                        placeholder={i18n.t('modules.servicePlanning.servicePlanning')}
                        dataqa="service-planning-module-name"
                        menuTitle
                    />
                    {props.flags.results?.userSelectedIcons
                        && (
                            <HorizontalFormContainer
                                inputClass="module-display-setting__setting"
                                title="Menu Icon"
                            >
                                <UserSelectedIconSelect
                                    id={state.menuItem.subType}
                                    type={state.menuItem.type}
                                    autoUpdate
                                />
                            </HorizontalFormContainer>
                        )}
                </div>
            </GeneralSection>
            <GeneralSection
                sectionClass="module-display-settings-list"
                dataqa="module-display-settings"
            >
                <div
                    id="module-display-setting__volunteer-welcome-message"
                // className="module-display-setting"
                >
                    <HorizontalFormContainer
                        inputClass="module-display-setting__setting volunteer-welcome-message"
                        title='Volunteer Welcome Message'
                    >
                        <div id="volunteerWelcomeMessage" className="form-control">
                            <ManagedInput
                                type="text"
                                placeholder='Volunteer Welcome Message'
                                name="volunteerWelcomeMessage"
                                value={state.module.volunteerWelcomeMessage}
                                maxLength={70}
                                onBlur={updateModuleParameterFromEvent}
                                onReturnDown={updateModuleParameterFromEvent}
                                autoFocus={false}
                                dataqa="service-planning-volunteer-welcome-message"
                            />
                        </div>
                    </HorizontalFormContainer>
                </div>

            </GeneralSection>
        </div>
    );

    return (
        <VersionBox
            name={props.versions.select.name}
            versionNumber={props.versions.select.version}
            saving={props.modules.inProgress}
            justSaved={props.modules.justSaved}
            menuSection="Modules"
            menuItem="ServicePlanningModule"
            previewCssClass="modules_service-planning"
            previewChild={previewServicePlanning}
            showPreview={state.showPreview}
            x
        >
            <div id="service-planning-module" className="content-inside">
                {bodyForm}
            </div>
        </VersionBox>
    );
};

export default ServicePlanningModule;